import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { STATIONS } from '../data/stations';
import { TrackReferenceSystem, Anchor } from '../utils/referenceSystem';
import { getPointAtPK } from '../utils/geometry';
import dataBalagneJSON from '../../public/data.json';
import dataCentraleJSON from '../../public/centrale.json';

interface TrackContextType {
    anchorsBalagne: Anchor[];
    anchorsCentrale: Anchor[];
    balagneSystem: TrackReferenceSystem;
    centraleSystem: TrackReferenceSystem;
    paths: {
        balagne: [number, number][];
        centrale: [number, number][];
    };
}

const TrackContext = createContext<TrackContextType | null>(null);



export const useTrack = () => {
    const context = useContext(TrackContext);
    if (!context) throw new Error('useTrack must be used within a TrackProvider');
    return context;
};

export const TrackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [rawPathBalagne, setRawPathBalagne] = useState<[number, number][]>([]);
    const [rawPathCentrale, setRawPathCentrale] = useState<[number, number][]>([]);

    const [anchorsBalagne, setAnchorsBalagne] = useState<Anchor[]>([]);
    const [anchorsCentrale, setAnchorsCentrale] = useState<Anchor[]>([]);

    useEffect(() => {
        if (dataBalagneJSON?.path) setRawPathBalagne(dataBalagneJSON.path as [number, number][]);
        if (dataCentraleJSON?.path) setRawPathCentrale(dataCentraleJSON.path as [number, number][]);
    }, []);

    useEffect(() => {
        if (rawPathBalagne.length > 0) {
            const path: [number, number][] = rawPathBalagne.map(c => [c[1], c[0]]);
            const initialAnchors = STATIONS.filter(s => s.line === 'Balagne' || s.line === 'Both').map(s => ({
                pk: s.pk,
                name: s.name,
                coord: s.coordinates ? [s.coordinates[1], s.coordinates[0]] : (getPointAtPK(path, s.pk, 119.9, 46.7) || path[0])
            })) as (Anchor & { name: string })[];
            setAnchorsBalagne(initialAnchors);
        }
    }, [rawPathBalagne]);

    useEffect(() => {
        if (rawPathCentrale.length > 0) {
            const path: [number, number][] = rawPathCentrale.map(c => [c[1], c[0]]);
            const initialAnchors = STATIONS.filter(s => s.line === 'Centrale' || s.line === 'Both').map(s => ({
                pk: s.pk,
                name: s.name,
                coord: s.coordinates ? [s.coordinates[1], s.coordinates[0]] : (getPointAtPK(path, s.pk, 0, 157.4) || path[0])
            })) as (Anchor & { name: string })[];
            setAnchorsCentrale(initialAnchors);
        }
    }, [rawPathCentrale]);

    const balagneSystem = useMemo(() => new TrackReferenceSystem(anchorsBalagne, rawPathBalagne, 'Balagne'), [anchorsBalagne, rawPathBalagne]);
    const centraleSystem = useMemo(() => new TrackReferenceSystem(anchorsCentrale, rawPathCentrale, 'Centrale'), [anchorsCentrale, rawPathCentrale]);

    const value = useMemo(() => ({
        anchorsBalagne,
        anchorsCentrale,
        balagneSystem,
        centraleSystem,
        paths: {
            balagne: rawPathBalagne,
            centrale: rawPathCentrale
        }
    }), [anchorsBalagne, anchorsCentrale, balagneSystem, centraleSystem, rawPathBalagne, rawPathCentrale]);

    return (
        <TrackContext.Provider value={value}>
            {children}
        </TrackContext.Provider>
    );
};
